import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPlayerProfileBySlug, PlayerCareerProfile } from '@/lib/supabase';
import { capture } from '@/lib/posthog';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const CATEGORY_META: { key: keyof PlayerCareerProfile['stats']; label: string; icon: string }[] = [
  { key: 'acerStars', label: 'Acer', icon: '⚡' },
  { key: 'catcherStars', label: 'Catcher', icon: '🧤' },
  { key: 'throwerStars', label: 'Thrower', icon: '🎯' },
];

export default function PublicPlayerProfile() {
  const { playerSlug } = useParams<{ playerSlug: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PlayerCareerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!playerSlug) return;
    setLoading(true);
    setNotFound(false);
    getPlayerProfileBySlug(playerSlug)
      .then(result => {
        if (!result) { setNotFound(true); setProfile(null); }
        else {
          setProfile(result);
          capture('player_profile_viewed', { player_slug: playerSlug, player_name: result.displayName });
        }
      })
      .catch(err => { console.error('[PublicPlayerProfile]', err); setNotFound(true); })
      .finally(() => setLoading(false));
  }, [playerSlug]);

  useEffect(() => {
    document.title = profile ? `${profile.displayName} - Player Profile | Throwball` : 'Player Profile | Throwball';
    return () => { document.title = 'Throwball Scorer'; };
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-3">🏐</div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Player Not Found</h1>
          <p className="text-sm text-text-secondary mb-4">
            No player matches "{playerSlug}".
          </p>
          <button onClick={() => navigate('/centre')} className="text-sm font-semibold text-info hover:underline">
            Back to Tournament Centre
          </button>
        </div>
      </div>
    );
  }

  const primaryTeam = profile.teams.length > 0
    ? [...profile.teams].sort((a, b) => b.matchesPlayed - a.matchesPlayed)[0]
    : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <button onClick={() => navigate('/centre')} className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span className="text-sm font-semibold">Back</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
          {/* Player Identity */}
          <div className="rounded-2xl p-6 text-center bg-surface border border-border">
            <div className="flex justify-center mb-4">
              {primaryTeam?.teamLogoUrl ? (
                <img src={primaryTeam.teamLogoUrl} alt={primaryTeam.teamShortName} className="w-20 h-20 rounded-full object-contain bg-surface-raised p-2" />
              ) : (
                <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryTeam?.teamColor ?? '#64748B' }}>
                  <span className="text-white text-3xl font-bold">{profile.displayName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-text-primary">{profile.displayName}</p>
            {profile.teams.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-1.5 mt-3">
                {profile.teams.map(t => (
                  <span key={t.teamId} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: `${t.teamColor}20`, color: t.teamColor }}>
                    {t.teamShortName}{t.jerseyNumbers.length > 0 ? ` #${t.jerseyNumbers.join('/')}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Career Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatCard label="Matches" value={profile.totalMatches} />
            <StatCard label="Wins" value={profile.wins} accent="emerald" />
            <StatCard label="Losses" value={profile.losses} accent="red" />
            <StatCard label="Player of Match" value={profile.playerOfMatchCount} accent="amber" />
          </div>

          {/* Total Stars */}
          <div className="rounded-xl p-5 text-center bg-warning/5 border-2 border-warning/30">
            <p className="text-[10px] font-bold uppercase tracking-wider text-warning mb-1">Total Career Stars</p>
            <p className="text-4xl font-bold text-warning">{profile.stats.totalStars}</p>
          </div>

          {/* Category Breakdown */}
          <div className="bg-surface rounded-xl border border-border shadow-sm p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-3">Stats Breakdown</p>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORY_META.map(cat => (
                <div key={cat.key} className="bg-background rounded-lg border border-border p-3 text-center">
                  <p className="text-lg">{cat.icon}</p>
                  <p className="text-lg font-bold text-text-primary">{profile.stats[cat.key]}</p>
                  <p className="text-[10px] text-text-secondary uppercase font-medium">{cat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Team History */}
          {profile.teams.length > 0 && (
            <div className="bg-surface rounded-xl border border-border shadow-sm p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-3">Team History</p>
              <div className="space-y-2">
                {[...profile.teams].sort((a, b) => b.matchesPlayed - a.matchesPlayed).map(t => (
                  <div key={t.teamId} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {t.teamLogoUrl ? (
                        <img src={t.teamLogoUrl} alt={t.teamShortName} className="w-7 h-7 rounded-full object-contain bg-surface-raised p-1 flex-shrink-0" />
                      ) : (
                        <span className="w-6 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: t.teamColor }} />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate" style={{ color: t.teamColor }}>{t.teamName}</p>
                        {t.jerseyNumbers.length > 0 && (
                          <p className="text-[10px] text-text-muted">Jersey #{t.jerseyNumbers.join(', #')}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-text-secondary flex-shrink-0">{t.matchesPlayed} matches</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Match History */}
          {profile.recentMatches.length > 0 && (
            <div className="bg-surface rounded-xl border border-border shadow-sm p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-3">Match History</p>
              <div className="space-y-2">
                {profile.recentMatches.slice(0, 20).map(m => (
                  <div key={m.matchId} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-[10px] text-text-muted flex-shrink-0">#{m.matchNumber}</span>
                      <span className="text-xs font-semibold truncate" style={{ color: m.teamColor }}>{m.teamShortName}</span>
                      <span className="text-[10px] text-text-secondary flex-shrink-0">vs</span>
                      <span className="text-xs font-semibold truncate" style={{ color: m.opponentColor }}>{m.opponentShortName}</span>
                      <span className="text-[10px] text-text-muted flex-shrink-0">{formatDate(m.date)}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {m.starsInMatch > 0 && <span className="text-[10px] font-bold text-warning">⭐{m.starsInMatch}</span>}
                      {m.result !== '-' && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.result === 'W' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                          {m.result === 'W' ? 'WIN' : 'LOSS'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {profile.recentMatches.length > 20 && (
                <p className="text-center text-[10px] text-text-muted mt-2">+{profile.recentMatches.length - 20} more matches</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string | number; accent?: 'emerald' | 'red' | 'amber' }) {
  const colors = { emerald: 'text-success', red: 'text-error', amber: 'text-warning' };
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-3 text-center">
      <p className="text-[9px] font-bold uppercase tracking-wider text-text-secondary mb-1">{label}</p>
      <p className={`text-xl font-bold ${accent ? colors[accent] : 'text-text-primary'}`}>{value}</p>
    </div>
  );
}
